import { createContext, useContext, useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in (from localStorage)
    const storedUser = localStorage.getItem('admin_user');
    const token = localStorage.getItem('adminToken');
    
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
        // Optionally verify token with backend
        verifyToken(token);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        clearAuth();
      }
    }
    setLoading(false);
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        // Token invalid, clear auth
        clearAuth();
        return;
      }
      
      const data = await response.json();
      if (data.success && data.user) {
        setUser(data.user);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
      }
    } catch (error) {
      console.error('Error verifying token:', error);
    }
  };

  const clearAuth = () => {
    setUser(null);
    localStorage.removeItem('admin_user');
    localStorage.removeItem('adminToken');
  };

  const login = async (email, password, extra = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ ...extra, email, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setUser(data.user);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
        localStorage.setItem('adminToken', data.token);
        return { success: true, user: data.user };
      } else {
        return { 
          success: false, 
          error: data.message || 'Email ou mot de passe incorrect' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Une erreur est survenue. Veuillez réessayer.' 
      };
    }
  };

  const register = async (name, email, password, passwordConfirmation, extra = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          ...extra,
          name, 
          email, 
          password,
          password_confirmation: passwordConfirmation
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setUser(data.user);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
        localStorage.setItem('adminToken', data.token);
        return { success: true, user: data.user };
      } else {
        return { 
          success: false, 
          error: data.message || 'Erreur lors de l\'inscription',
          errors: data.errors
        };
      }
    } catch (error) {
      console.error('Register error:', error);
      return { 
        success: false, 
        error: 'Une erreur est survenue. Veuillez réessayer.' 
      };
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('adminToken');
    
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    clearAuth();
  };

  const updateProfile = async (profileData) => {
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setUser(data.user);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
        return { success: true };
      } else {
        return { 
          success: false, 
          error: data.message || 'Erreur lors de la mise à jour',
          errors: data.errors
        };
      }
    } catch (error) {
      console.error('Update profile error:', error);
      return { 
        success: false, 
        error: 'Une erreur est survenue. Veuillez réessayer.' 
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
